import React from 'react';
import { TabbedShowLayout, Tab, TextField, FunctionField, ReferenceField, ArrayField, Labeled, useRecordContext } from 'react-admin';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import Show, { ShowActions } from '../common/views/Show';
import Datagrid from '../common/Datagrid';
import DateField from '../common/fields/DateField';
import MultilineTextField from '../common/fields/MultilineTextField';
import NumberCurrencyField from '../common/fields/NumberCurrencyField';
import InvoiceActionsButtons from './InvoiceActionsButtons';

import StatusField from './fields/StatusField';
import PeppolStatusField from './fields/PeppolStatusField';
import ServiceTypeField from './fields/ServiceTypeField';
import VatField from './fields/VatField';

import { API_URL } from '../../services/settings';

const iframeSx = {
    'iframe': {
        width: '100%',
        minHeight: '100vh',
        border: 'none'
    }
};

const InvoiceActions = (props) => {
    const record = useRecordContext();

    return (
        <ShowActions {...props} hasEdit={record?.status === 'draft'}>
            <InvoiceActionsButtons record={record} />
        </ShowActions>
    );
};

const InvoicePDF = () => {
    const record = useRecordContext();

    return (
        <Box sx={iframeSx}>
            <iframe
                title="PDF"
                src={`${API_URL}/invoices/${record?.id}/invoice${record?.number ? `_${record.number}` : ''}.pdf`}
            />
        </Box>
    );
};

const LineList = (props) => (
    <ArrayField {...props} source="lines">
        <Datagrid bulkActionButtons={false}>
            <MultilineTextField source="description" />
            <TextField source="qty" label="Quantity" />
            <FunctionField label="Price" render={record => <NumberCurrencyField record={{ currency: props?.record?.currency, ...record }} source="unit_price" />} />
            <VatField label="VAT" />
            <FunctionField label="Total excl. VAT" render={record => <NumberCurrencyField record={{ currency: props?.record?.currency, value: record.qty * record.unit_price }} source="value" />} />
        </Datagrid>
    </ArrayField>
);

const PeppolValidationStatus = () => {
    const record = useRecordContext();

    if (!record?.peppol_validated) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                PEPPOL validation not yet performed. Click "Validate PEPPOL" button to check compliance.
            </Alert>
        );
    }

    const hasErrors = record?.peppol_validation_errors && record.peppol_validation_errors.length > 0;
    const hasPdfUblMismatch = record?.peppol_validation_errors?.some(err => err.id === 'PDF-UBL-MISMATCH');

    return (
        <Box sx={{ mb: 2 }}>
            {hasPdfUblMismatch ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <strong>CRITICAL ERROR: PDF and UBL Mismatch</strong>
                    <br />
                    The PDF and UBL calculations do not match. This invoice cannot pass PEPPOL validation until this is resolved.
                </Alert>
            ) : hasErrors ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <strong>PEPPOL Validation Failed</strong>
                    <br />
                    {record.peppol_validation_errors.length} error(s) found
                </Alert>
            ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <strong>PEPPOL Validation Passed ✓</strong>
                    <br />
                    Invoice is compliant with PEPPOL standards
                </Alert>
            )}

            <Box sx={{ mb: 2 }}>
                <Chip
                    label={`Validated at: ${new Date(record?.peppol_validated_at).toLocaleString()}`}
                    color={hasErrors ? 'error' : 'success'}
                    variant="outlined"
                />
            </Box>

            {hasErrors && (
                <Card sx={{ mt: 2, backgroundColor: hasPdfUblMismatch ? '#ffebee' : '#fff3e0' }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Validation Errors
                        </Typography>
                        <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {record.peppol_validation_errors.map((err, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        border: '1px solid #ddd',
                                        borderLeft: err.level === 'FATAL_ERROR' ? '4px solid #d32f2f' : '4px solid #f57c00',
                                        borderRadius: 1,
                                        backgroundColor: '#fafafa'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <Chip
                                            label={err.id}
                                            size="small"
                                            color={err.level === 'FATAL_ERROR' ? 'error' : 'warning'}
                                        />
                                        <Chip
                                            label={err.level}
                                            size="small"
                                            variant="outlined"
                                            color={err.level === 'FATAL_ERROR' ? 'error' : 'warning'}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                        {err.description}
                                    </Typography>
                                    {err.details && err.details.length > 0 && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #ddd' }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                                                Details:
                                            </Typography>
                                            {err.details.map((detail, detailIdx) => (
                                                <Typography key={detailIdx} variant="caption" sx={{ display: 'block', color: '#999' }}>
                                                    {detail.type}: PDF={detail.pdf?.toFixed ? detail.pdf.toFixed(2) : detail.pdf}, UBL={detail.ubl?.toFixed ? detail.ubl.toFixed(2) : detail.ubl}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

const InvoiceShow = (props) => (
    <Show actions={<InvoiceActions />} {...props}>
        <TabbedShowLayout>
            <Tab label="PDF">
                <Labeled fullWidth>
                    <InvoicePDF />
                </Labeled>
            </Tab>
            <Tab label="Summary">
                <PeppolValidationStatus />
                <TextField label="Id" source="id" />
                <TextField label="UUID" source="uuid" />
                <TextField source="number" />
                <StatusField label="Status" />
                <ServiceTypeField label="Service" />
                <PeppolStatusField label="PEPPOL Status" />
                <TextField source="source" />
                <ReferenceField label="Billing company" source="billing_company_id" reference="companies" allowEmpty>
                    <TextField source="name" />
                </ReferenceField>
                <ReferenceField label="Customer" source="customer_id" reference="customers" allowEmpty>
                    <TextField source="name" />
                </ReferenceField>
                <ReferenceField label="Invoicing address" reference="addresses" source="invoicing_address_id" allowEmpty>
                    <FunctionField render={record => (
                        <>
                            {record.name}<br />
                            {record.street}<br />
                            {record.zipcode} {record.city}<br />
                            {record.country}<br />
                            {record.vat}
                        </>
                    )} />
                </ReferenceField>
                <TextField source="number" />
                <DateField source="invoiced_at" />
                <DateField source="due_date" />
                <TextField label="Customer Reference" source="purchase_order" />
                <LineList label={false} />
                <NumberCurrencyField source="total_untaxed" />
                <NumberCurrencyField source="total_tax" />
                <NumberCurrencyField source="total" />
                <MultilineTextField source="terms" />
                <MultilineTextField source="notes" />
                <DateField label="Created at" source="created_at" />
                <DateField label="Updated at" source="updated_at" />
            </Tab>
        </TabbedShowLayout>
    </Show>
);

export default InvoiceShow;
